import { ArrowLeft } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import LanguageToggleLink from "../components/LanguageToggleLink";
import Seo from "../components/Seo";
import ScandicDevLogo from "../components/ScandicDevLogo";
import { useLanguage } from "../context/LanguageContext";
import { getAlternateLanguagePath, getHomePath, legalPageSeo } from "../lib/site";

// FULL German Impressum content - Complete document
const contentDE = {
  title: "Impressum",
  back: "Zurück",
  lastUpdated: "Letzte Aktualisierung: 01. Januar 2026",
  content: `
## IMPRESSUM

**„SCANDICDEV" powered by „Scandic Pilot"**

„SCANDICDEV" ist ein Dienst der Media Division der SCANDIC FINANCE GROUP LIMITED in Zusammenarbeit mit der LEGIER Beteiligungs GmbH aus der Bundesrepublik Deutschland.

Für die Bereitstellung, den Betrieb und die Inhalte von „SCANDICDEV" gelten die folgenden Verantwortlichkeiten und Kontaktdaten:

---

### LEGIER Beteiligungs GmbH

Kurfürstendamm 14, 10719 Berlin, Bundesrepublik Deutschland

Handelsregister Berlin-Charlottenburg HRB 57837 / Deutschland

Umsatzsteuer-Identifikationsnummer: DE 413 445 833

Telefon: +49 (0) 30 99211‑3 469

Fax: +49 (0) 30 99211‑3 225

E-Mail: info@LegierGroup.com

Die LEGIER Beteiligungs GmbH betreibt die technische Plattform.

---

### SCANDIC FINANCE GROUP

Raum 10, Einheit A, 7. Etage Harbour Sky, 28 Sze Shan Street, Yau Tong, HK/SAR/VR China

Telefon Zentrale Schweiz – Zürich: +41 44 7979 99 85

E-Mail: Office@ScandicFinance.Global

Die SCANDIC FINANCE GROUP LIMITED fungiert als betreibender Dienstleister, ist für die technische Bereitstellung von „SCANDICDEV" verantwortlich und ist Herausgeber der LEGIER-Medien; als verantwortliche Stelle im Sinne von § 7 (1) DDG übernimmt sie die redaktionelle Verantwortung für „SCANDICDEV".

---

### SCANDIC ASSETS FZCO

Dubai Silicon Oasis DDP, Gebäude A1/A2

Dubai – 342001 Vereinigte Arabische Emirate

Telefon: +971 56 929 86 90

E-Mail: info@ScandicAssets.dev

Tätig als nicht operativer Dienstleister für Infrastruktur und Hosting.

---

### Weitere Kooperationspartner

In einzelnen Ländern arbeiten lokale Tochtergesellschaften oder Partner mit, darunter SCANDIC DATA. SCANDIC DATA fungiert unter anderem als Rechenzentrum und stellt sicher, dass die rechtlichen Anforderungen der jeweiligen Rechtsordnung erfüllt werden.

---

### Anbieterkennzeichnung gemäß § 5 DDG und § 18 Abs. 2 MStV

Bitte richten Sie Ihre schriftlichen Anfragen gegebenenfalls ausschließlich an Info@ScandicDev.com. Ihre E-Mail wird über ein Rechenzentrum in den Hochsicherheitsanlagen von Iron Mountain an die zuständige Abteilung weitergeleitet und entsprechend dem Betreff Ihrer Anfrage beantwortet. Alle Systeme werden rund um die Uhr durch selbstlernende KI und Cybersicherheitslösungen unseres Partners DARKTRACE geschützt.

**Inhaltlich Verantwortlicher gemäß § 18 Abs. 2 MStV:** die Redaktion von „SCANDICDEV" (Medienbereich der SCANDIC FINANCE GROUP). Die Redaktion wird von einer Chefredaktion geleitet; Anfragen an die Redaktion können an info@ScandicAssets.dev gerichtet werden.

Zur Gewährleistung der journalistischen Sorgfaltspflicht gelten die Bestimmungen des deutschen Medienstaatsvertrags sowie die presserechtlichen Bestimmungen des Landes Berlin.

**Jugendschutzbeauftragter:** Bei Fragen zum Jugendschutz wenden Sie sich bitte an den Jugendschutzbeauftragten der LEGIER Beteiligungs GmbH. Erreichbar unter info@ScandicAssets.dev.

---

### Aufsichtsbehörde

Zuständige Aufsichtsbehörde für journalistisch-redaktionelle Inhalte ist die Medienanstalt Berlin-Brandenburg (mabb), Kleine Präsidentenstraße 1, 10178 Berlin, Bundesrepublik Deutschland.

---

### Rechtsgrundlagen

- **Digital Services Act (DDG):** Regelt Pflichten hinsichtlich der Identifizierung von Anbietern, Haftungsregeln und Transparenzanforderungen.

- **Medienstaatsvertrag (MStV):** Enthält Anforderungen für Rundfunk und Telemedien in Deutschland, insbesondere hinsichtlich der Verantwortung für Inhalte.

- **EU-Datenschutz-Grundverordnung (DSGVO):** Ergänzende nationale Datenschutzgesetze (z. B. das Bundesdatenschutzgesetz): schützen personenbezogene Daten und legen die Bedingungen für deren Verarbeitung fest.

- **E-Privacy-Richtlinie und Telekommunikations- und Digitaldienst-Datenschutzgesetz (TDDDG):** Regeln die Speicherung und das Auslesen von Informationen auf Endgeräten (Cookies, Werbe-IDs). Für Cookies, die nicht unbedingt erforderlich sind, wird Ihre Einwilligung eingeholt.

- **Digital Services Act (DSA) der Europäischen Union:** Legt zusätzliche Transparenz- und Sorgfaltspflichten für Online-Plattformen fest, darunter ein internes Beschwerdesystem und jährliche Transparenzberichte.

- **Regionale Gesetze außerhalb der EU:** Für Nutzer in Staaten wie den Vereinigten Arabischen Emiraten, Bahrain, Saudi-Arabien, Ägypten, Nigeria, Brasilien, den USA, Kanada, China, Japan, Südkorea und zahlreichen anderen Ländern gelten zusätzliche Datenschutzgesetze (zum Beispiel: PDPL, CCPA/CPRA, PIPEDA, LGPD, PIPL). LEGIER berücksichtigt in jedem Fall die strengen Anforderungen der lokalen Gesetzgebung und wendet im Zweifelsfall das strengste Schutzniveau an, wie in unseren FAQs zum Datenschutz ausführlich erläutert.

---

### Haftung für Inhalte und Links

Gemäß §§ 8–10 DDG sind wir als Diensteanbieter nicht verpflichtet, übermittelte oder gespeicherte fremde Informationen ständig zu überwachen oder nach Umständen zu forschen, die auf eine rechtswidrige Tätigkeit hinweisen. Verpflichtungen zur Entfernung oder Sperrung der Nutzung von Informationen nach den allgemeinen Gesetzen bleiben hiervon unberührt. Eine diesbezügliche Haftung ist erst ab dem Zeitpunkt der Kenntnis einer konkreten Rechtsverletzung möglich. Bei Bekanntwerden von entsprechenden Rechtsverletzungen werden wir diese Inhalte umgehend entfernen.

Wir übernehmen keine Haftung für die Inhalte externer Links; für den Inhalt der verlinkten Seiten sind ausschließlich deren Betreiber verantwortlich.

---

### Urheberrecht und gewerbliche Schutzrechte

Die Inhalte und die Struktur der LEGIER-Websites, Apps und Tageszeitungen sind urheberrechtlich geschützt. Eine Vervielfältigung oder Verbreitung bedarf der vorherigen schriftlichen Zustimmung der LEGIER Beteiligungs GmbH oder der jeweiligen vertretenen Unternehmen.

Textpassagen dürfen im Rahmen des Zitatrechts (§ 51 UrhG) unter Angabe der Quelle verwendet werden. Der kommerzielle Weiterverkauf, insbesondere automatisierte Text- und Data-Mining-Nutzungen (§ 44b UrhG), ist untersagt oder erfordert den Erwerb einer Nutzungslizenz. Anfragen richten Sie bitte an info@ScandicAssets.dev. Alle weiteren Rechte vorbehalten.

---

### Datenschutz

Die Verarbeitung personenbezogener Daten unterliegt der DSGVO und den jeweils geltenden nationalen Gesetzen. Die SCANDIC FINANCE GROUP LIMITED ist als Dienstleister im Sinne der DSGVO verantwortlich. Kontaktdaten: Info@ScandicDev.com.

Einzelheiten zum Umgang mit personenbezogenen Daten, zu den verwendeten Cookies und Tracking-Technologien, zu den Rechtsgrundlagen und zu Ihren datenschutzrechtlichen Rechten (Auskunft, Berichtigung, Löschung, Einschränkung der Verarbeitung, Datenübertragbarkeit, Widerspruch und Widerruf) finden Sie in der Datenschutzerklärung.

Die Datenschutzerklärung erläutert zudem internationale Datenübermittlungen und lokale Besonderheiten in den mehr als 40 Ländern, in denen LEGIER tätig ist.

Ein Datenschutzbeauftragter der LEGIER Beteiligungs GmbH ist unter Info@ScandicDev.com erreichbar.

Sie haben das Recht, eine Beschwerde bei einer Datenschutzaufsichtsbehörde einzureichen, z. B. beim Berliner Beauftragten für Datenschutz und Informationsfreiheit.

---

### Cookie- und Einwilligungsmanagement

Die App „SCANDICDEV" finanziert ihre Inhalte durch Werbung und die App. Beim ersten Start der App erscheint daher ein Einwilligungsbanner, das Sie über die verwendeten Cookies und Tracking-Technologien informiert und Ihnen ermöglicht, Ihre Einwilligung detailliert zu erteilen oder zu verweigern.

Ohne Ihre Einwilligung werden nur technisch notwendige Cookies gesetzt (§ 25 Abs. 2 TDDDG). Weitere Informationen zur Funktionalität, zu Real-Time-Bidding-Verfahren und zur Dauer von Cookies finden Sie in der Datenschutzerklärung und den FAQs.

---

### Einhaltung internationaler Rechtsvorschriften

**AI powered by „SCANDICDEV" – Überblick**

„SCANDICDEV" erscheint in mehr als 115 Tageszeitungen auf allen Kontinenten und in zahlreichen Sprachen. Die folgenden Beispiele geben einen Überblick über wichtige Titel nach Kontinenten:

**Europa:** Berliner Tageszeitung (Deutschland), Frankfurter Tageszeitung (Deutschland), Journal de Bruxelles (Belgien), Volkswacht Bodensee und Zürcher Nachrichten (Schweiz), Athens News (Griechenland), Paris Soir und Le Pays de France (Frankreich), Il Messaggiere und Domenica del Corriere (Italien), The Prague Post (Tschechische Republik), Nottingham Guardian und The National Times (Vereinigtes Königreich), The Irish Press (Irland), Stockholms Dagblad (Schweden), Gazeta Lodz (Polen) und CoinPress.News (paneuropäische Finanzberichterstattung).

**Nordamerika:** Calgary Tribune (Kanada), San Francisco Frontiers (USA), The Fort Worth Press (USA) und weitere regionale Titel.

**Südamerika:** Jornal do Brasília (Brasilien), Bogotá Tiempo (Kolumbien), La Gaceta de México (Mexiko), El Siglo Futuro (Chile) und andere.

**Afrika:** Courrier Égypte (Ägypten), Nigeria Daily und Ghanaian Herald berichten über regionale Themen; einige Titel erscheinen auf Französisch oder Arabisch.

**Naher Osten:** Alhayat.Media (panarabische Ausgabe), Bahrain Telegraph (Bahrain), Riyadh Gazette (Saudi-Arabien) und andere lokale Ausgaben.

**Asien:** Madras Times (Indien), Seoul Gazette (Südkorea), Morning Chronicle (Singapur), Japan Review (Japan), Viet News (Vietnam) und andere.

**Ozeanien:** The National Times (Australien) und Pacific Herald (Neuseeland) liefern Informationen zu Politik, Wirtschaft und Kultur der Region.

In allen Ländern, in denen die Datenschutzerklärung von „SCANDICDEV" veröffentlicht ist, gelten unterschiedliche Rechtssysteme. LEGIER richtet seine Aktivitäten konsequent nach den strengsten geltenden Rechtsvorschriften aus.

---

### Regionale Datenschutzgesetze

**Europäische Union:** Innerhalb der Europäischen Union gelten die DSGVO sowie die nationalen Datenschutzgesetze (z. B. das Bundesdatenschutzgesetz in Deutschland, das österreichische Datenschutzgesetz 2018, das revidierte Schweizer Datenschutzgesetz, das französische Gesetz „Informatique et Libertés", das italienische Datenschutzgesetz, das spanische Ley Orgánica de Protección de Datos Personales, das portugiesische Lei de Proteção de Dados Pessoais, die belgische Allgemeine Datenschutzverordnung, das griechische Gesetz 4624/2019, das polnische Datenschutzgesetz, das tschechische Gesetz Nr. 110/2019, das irische Datenschutzgesetz von 2018, das schwedische Datenschutzgesetz 2018:218 und das britische Datenschutzgesetz von 2018).

**Nordamerika:** In Nordamerika unterliegen unsere Inhalte dem kanadischen Gesetz zum Schutz personenbezogener Daten und elektronischer Dokumente (PIPEDA) sowie den Datenschutzgesetzen der US-Bundesstaaten, insbesondere dem kalifornischen Gesetz zum Schutz der Privatsphäre von Verbrauchern (CCPA) in der durch das kalifornische Datenschutzrechtsgesetz (California Privacy Rights Act, CPRA) geänderten Fassung. Für Mexiko gilt das Ley Federal de Protección de Datos Personales en Posesión de los Particulares von 2010.

**Südamerika:** In Südamerika orientieren wir uns an der brasilianischen Lei Geral de Proteção de Dados (LGPD) von 2018/2020, dem argentinischen Datenschutzgesetz Nr. 25.326 (2000), dem uruguayischen Datenschutzgesetz Nr. 18.331 (2008), der peruanischen Datenschutzverordnung Nr. 29733 (2011), die chilenische Datenschutzreform (Ley 21.719, voraussichtlich Inkrafttreten 2026) sowie weitere nationale Sonderregelungen für Presse und Medien.

**Afrika:** In Afrika gelten unter anderem die nigerianischen Gesetze (Data Protection Act 2023 und Press Council Act 1992), das ghanaische Datenschutzgesetz von 2012, das südafrikanische Gesetz zum Schutz personenbezogener Daten (POPIA), das ägyptische Datenschutzgesetz Nr. 151/2020 sowie die Pressegesetze der jeweiligen Staaten.

**Naher Osten:** Im Nahen Osten orientieren wir uns am Datenschutzgesetz der Vereinigten Arabischen Emirate (Bundesgesetzesdekret Nr. 45 von 2021), dem saudiarabischen Gesetz zum Schutz personenbezogener Daten (2021), dem katarischen Gesetz Nr. 13/2016 zum Schutz der Privatsphäre, das omanische Datenschutzgesetz (Königliches Dekret 6/2022), die bahrainischen Pressevorschriften (Gesetzesdekret 47/2002) sowie die jeweiligen straf- und medienrechtlichen Bestimmungen.

**Asien:** In Asien gelten, je nach Ausgabe, das chinesische Gesetz zum Schutz personenbezogener Daten (PIPL, 2021), das indische Gesetz zum Schutz digitaler personenbezogener Daten von 2023, das japanische Gesetz zum Schutz personenbezogener Daten (APPI), das südkoreanische Gesetz zum Schutz personenbezogener Daten (PIPA) von 2011, die Datenschutzgesetze von Hongkong (PDPO), Singapur (PDPA 2012), das vietnamesische Dekret 13/2023 ND-CP, das malaysische Gesetz zum Schutz personenbezogener Daten von 2010, das australische Datenschutzgesetz von 1988 und das neuseeländische Datenschutzgesetz von 2020.

Alle LEGIER-Redaktionen halten sich an den Pressekodex, beachten die Grenzen des rechtlich Zulässigen und setzen die lokalen Gesetze zum Jugendschutz, Verbraucherschutz und zur Netzwerksicherheit um.

---

### Barrierefreiheit und Ethik

Die „SCANDICDEV"-App unterstützt die Bestimmungen zur Barrierefreiheit: https://LegierGroup.com/en/vulnerable-customers

Die „SCANDICDEV"-App unterstützt die Bestimmungen des Modern Slavery Act: https://LegierGroup.com/en/modern-slavery-act

Die „SCANDICDEV"-App unterstützt die Bestimmungen zur KI-Ethik: https://ScandicData.com/ai-ethics

---

### Zentrale Kontaktstellen gemäß dem Digital Services Act (DSA)

Gemäß den Artikeln 11 und 12 DSA benennt die „SCANDICDEV"-App folgende zentrale Kontaktstellen:

**Kontaktstelle für Nutzer (Art. 12 DSA):** LEGIER-Kundendienst – World Conference, E-Mail: info@ScandicAssets.dev (Deutsch, Englisch)

**Ansprechpartner für Behörden (Art. 11 DSA):** LEGIER Beteiligungs GmbH – DSA-Kontaktstelle, E-Mail: info@ScandicAssets.dev (Deutsch, Englisch)

**Meldung illegaler Inhalte:** Über die App-Funktion „Inhalt melden" oder per E-Mail an info@ScandicAssets.dev (Alle Sprachen der App)

Anfragen an diese Kontaktstellen werden innerhalb der gesetzlichen Frist bearbeitet. Beschwerden von Nutzern über redaktionelle Inhalte können über ein internes Beschwerdesystem in der App eingereicht werden; sie können sich auch an Schlichtungsstellen oder Aufsichtsbehörden wenden.

---

### NetzDG (Netzwerkdurchsetzungsgesetz)

Die App „SCANDICDEV" erfüllt die gesetzlichen Anforderungen des Netzdurchsetzungsgesetzes zur Verbesserung der Rechtsdurchsetzung in sozialen Netzwerken. Beschwerden über offensichtlich rechtswidrige Inhalte können direkt in der App gemeldet werden. Die Meldungen werden unverzüglich geprüft und rechtswidrige Inhalte entfernt. Weitere Informationen finden Sie im Abschnitt „Beschwerdeverfahren" der App.

---

### Serverstandorte und Sicherheit

Die Server für unsere digitalen Angebote befinden sich in Hochsicherheits-Rechenzentren in Boston (USA) und Zürich (Schweiz). Der Betrieb erfolgt durch den Schweizer Host ASPECTRA unter Verwendung moderner Verschlüsselung (AES mit 4096-Bit-Schlüsseln im CBC-Modus).

Zudem werden alle Daten in den Datentresoren von Iron Mountain gespiegelt und rund um die Uhr durch KI-basierte Systeme überwacht. Im Falle eines Angriffs kann der Betrieb kurzfristig auf redundante Server verlagert werden.

Zahlungen für Abonnements werden über zertifizierte Zahlungsdienstleister wie PayPal, Stripe, Klarna oder Kreditkartenunternehmen abgewickelt; Kreditkartendaten werden nicht auf unseren Servern gespeichert.

Über exklusive Partner versorgen die „SCANDICDEV"-Dienste internationale Börsen wie die Frankfurter Wertpapierbörse, die New York Stock Exchange, die Tokyo Stock Exchange, die London Stock Exchange und andere. Aktienkurse und Finanzinformationen werden mit größtmöglicher Sorgfalt zusammengestellt; es wird jedoch keine Gewähr für Aktualität, Richtigkeit oder Vollständigkeit übernommen.

---

### Schutz von Minderjährigen

„SCANDICDEV" unterstützt Initiativen zum Schutz von Minderjährigen. Die App bietet Filter- und Kontrollmöglichkeiten für Eltern sowie Verweise auf die ICRA-Klassifizierung (Internet Content Rating Association).

---

### Rechtswirksamkeit

Dieses Impressum ist Teil des digitalen Angebots von „SCANDICDEV". Sollten einzelne Formulierungen nicht der aktuellen Rechtslage entsprechen, bleiben die übrigen Teile des Impressums inhaltlich und gültig unberührt.
  `
};

// FULL English Imprint content - Complete document
const contentEN = {
  title: "Imprint",
  back: "Back",
  lastUpdated: "Last updated: 01 January 2026",
  content: `
## IMPRINT

**"SCANDICDEV" powered by "Scandic Pilot"**

"SCANDICDEV" is a service of the Media Division of SCANDIC FINANCE GROUP LIMITED in cooperation with LEGIER Beteiligungs GmbH from the Federal Republic of Germany. The following responsibilities and contact details apply to the provision, operation and content of ScandicDev:

---

### LEGIER Beteiligungs GmbH

Kurfürstendamm 14, 10719 Berlin, Federal Republic of Germany

Commercial Register Berlin-Charlottenburg HRB 57837 / Germany

VAT identification number: DE 413 445 833

Telephone: +49 (0) 30 99211-3 469

Fax: +49 (0) 30 99211-3 225

E-mail: info@LegierGroup.com

LEGIER Beteiligungs GmbH operates the technical platform.

---

### SCANDIC FINANCE GROUP

Room 10, Unit A, 7/F Harbour Sky, 28 Sze Shan Street, Yau Tong, HK/SAR/PRC

Telephone HQ Switzerland – Zurich: +41 44 7979 99 85

E-mail: Office@ScandicFinance.Global

SCANDIC FINANCE GROUP LIMITED acts as the operating service provider, is responsible for the technical provision of "SCANDICDEV" and is the publisher of the LEGIER media and, as the responsible body within the meaning of § 7 (1) DDG, assumes editorial responsibility for "SCANDICDEV".

---

### SCANDIC ASSETS FZCO

Dubai Silicon Oasis DDP, Building A1/A2

Dubai – 342001 United Arab Emirates

Telephone: +971 56 929 86 90

E-mail: info@ScandicAssets.dev

Acts as a non-operating service provider for infrastructure and hosting.

---

### Other cooperation partners

In individual countries, local subsidiaries or partners cooperate, including SCANDIC DATA. SCANDIC DATA serves, among other things, as a data centre and ensures that the legal requirements of the respective jurisdiction are met.

---

### Responsible provider labelling pursuant to § 5 DDG and § 18 (2) MStV

If necessary, please direct your written enquiries exclusively to Info@ScandicDev.com. Your e-mail will be forwarded via a data centre in the high-security facilities of Iron Mountain to the responsible department and answered in accordance with the subject of your enquiry. All systems are protected around the clock by self-learning AI and cyber security solutions from our partner DARKTRACE.

**Person responsible for content pursuant to § 18 (2) MStV:** the "SCANDICDEV" editorial team (media division of SCANDIC FINANCE GROUP). The editorial team is led by a chief editorial team; enquiries to the editorial team can be addressed to info@ScandicAssets.dev.

For ensuring journalistic due diligence, the provisions of the German Media State Treaty and the press law provisions of the State of Berlin apply.

**Youth protection officer:** For questions about the protection of young people, please contact the youth protection officer of LEGIER Beteiligungs GmbH. Reachable via info@ScandicAssets.dev.

---

### Supervisory authority

The competent supervisory authority for journalistic-editorial content is the Medienanstalt Berlin-Brandenburg (mabb), Kleine Präsidentenstraße 1, 10178 Berlin, Federal Republic of Germany.

---

### Legal bases

- **Digital Services Act (DDG):** Regulates duties regarding provider identification, liability rules and transparency requirements.

- **Media State Treaty (MStV):** Contains requirements for broadcasting and telemedia in Germany, in particular regarding responsibility for content.

- **EU General Data Protection Regulation (GDPR):** Complementary national data protection laws (for example the German Federal Data Protection Act): protect personal data and set out the conditions for its processing.

- **ePrivacy Directive and Telecommunications-Digital Services Data Protection Act (TDDDG):** Regulate the storage and reading of information on end devices (cookies, advertising IDs). Your consent will be obtained for cookies that are not strictly necessary.

- **European Union Digital Services Act (DSA):** Defines additional transparency and due diligence obligations for online platforms, including an internal complaints system and annual transparency reports.

- **Regional laws outside the EU:** For users in states such as the United Arab Emirates, Bahrain, Saudi Arabia, Egypt, Nigeria, Brazil, the USA, Canada, China, Japan, South Korea and numerous other countries, additional data protection laws apply (for example: PDPL, CCPA/CPRA, PIPEDA, LGPD, PIPL). LEGIER takes into account the strict requirements of local law in each case and, in cases of doubt, applies the strictest level of protection, as explained in detail in our data protection FAQs.

---

### Liability for content and links

According to §§ 8–10 DDG we, as a service provider, are not obliged to monitor transmitted or stored third-party information constantly or to investigate circumstances indicating illegal activity. Obligations to remove or block the use of information according to general laws remain unaffected. Liability is only possible from the time of knowledge of a specific infringement. Upon becoming aware of such infringements, we will remove this content immediately.

We do not assume liability for the content of external links; the providers of the linked pages are solely responsible for their content.

---

### Copyright and industrial property rights

The contents and structure of the LEGIER websites, apps and daily newspapers are protected by copyright. Reproduction or distribution requires the prior written consent of LEGIER Beteiligungs GmbH or the respective represented companies.

Text passages may be used within the framework of the quotation right (§ 51 UrhG) with citation of the source. Commercial resale, in particular automated text and data mining uses (§ 44b UrhG), is prohibited or requires the acquisition of a usage licence. Please direct enquiries to info@ScandicAssets.dev. All other rights reserved.

---

### Data protection

The processing of personal data is governed by the GDPR and the respective applicable national laws. The SCANDIC FINANCE GROUP LIMITED is responsible within the meaning of the GDPR as the service provider. Contact details: Info@ScandicDev.com.

Details on the handling of personal data, the cookies and tracking technologies used, the legal bases and your data subject rights (access, rectification, erasure, restriction of processing, data portability, objection and withdrawal) can be found in the privacy policy.

The privacy policy also explains international data transfers and local specifics in the more than 40 countries in which LEGIER operates.

A data protection officer of LEGIER Beteiligungs GmbH can be reached at Info@ScandicDev.com.

You have the right to lodge a complaint with a data protection supervisory authority, e.g. the Berlin Commissioner for Data Protection and Freedom of Information.

---

### Cookie and consent management

The "SCANDICDEV" app finances its content through advertising and the app. When the app is started for the first time, a consent banner therefore appears that informs you about the cookies and tracking technologies used and allows you to give or refuse consent granularly.

Without your consent only technically necessary cookies are set (§ 25 (2) TDDDG). Further information on the functionality, real-time bidding procedures and the duration of cookies can be found in the privacy policy and the FAQs.

---

### International legal compliance

**Newspapers AI powered by "SCANDICDEV" — overview**

"SCANDICDEV" is published in more than 115 daily newspapers on all continents and in numerous languages. The following examples provide an overview of important titles by continent (not exhaustive; the full list can be found on the website of the LEGIER Group):

**Europe:** Berliner Tageszeitung (Germany), Frankfurter Tageszeitung (Germany), Journal de Bruxelles (Belgium), Volkswacht Bodensee and Zürcher Nachrichten (Switzerland), Athens News (Greece), Paris Soir and Le Pays de France (France), Il Messaggiere and Domenica del Corriere (Italy), The Prague Post (Czech Republic), Nottingham Guardian and The National Times (United Kingdom), The Irish Press (Ireland), Stockholms Dagblad (Sweden), Gazeta Lodz (Poland) and CoinPress.News (pan-European financial reporting).

**North America:** Calgary Tribune (Canada), San Francisco Frontiers (USA), The Fort Worth Press (USA) and other regional titles.

**South America:** Jornal do Brasília (Brazil), Bogotá Tiempo (Colombia), La Gaceta de México (Mexico), El Siglo Futuro (Chile) and others.

**Africa:** Courrier Égypte (Egypt), Nigeria Daily and Ghanaian Herald (examples) report on regional topics; some titles appear in French or Arabic.

**Middle East:** Alhayat.Media (pan-Arab edition), Bahrain Telegraph (Bahrain), Riyadh Gazette (Saudi Arabia) and other local editions.

**Asia:** Madras Times (India), Seoul Gazette (South Korea), Morning Chronicle (Singapore), Japan Review (Japan), Viet News (Vietnam) and others.

**Oceania:** The National Times (Australia) and Pacific Herald (New Zealand) provide information on politics, economy and culture of the region.

In all countries where the Privacy Policy of "SCANDICDEV" are published, different legal systems apply. LEGIER consistently aligns its activities with the strictest applicable law.

---

### Regional Data Protection Laws

**Within the European Union:** the GDPR and the national data protection laws (e.g. the Federal Data Protection Act in Germany, the Austrian Data Protection Act 2018, the revised Swiss Data Protection Act, the French Loi "Informatique et Libertés", the Italian Data Protection Code, the Spanish Ley Orgánica de Protección de Datos Personales, the Portuguese Lei de Proteção de Dados Pessoais, the Belgian General Data Protection Regulation, the Greek Law 4624/2019, the Polish Data Protection Act, the Czech Act No. 110/2019, the Irish Data Protection Act 2018, the Swedish Data Protection Act 2018:218 and the British Data Protection Act 2018) apply.

**In North America:** our content is subject to the Canadian Personal Information Protection and Electronic Documents Act (PIPEDA) as well as the data protection laws of the US states, in particular the California Consumer Privacy Act (CCPA) in its version amended by the California Privacy Rights Act (CPRA). For Mexico, the Ley Federal de Protección de Datos Personales en Posesión de los Particulares of 2010 applies.

**In South America:** we follow the Brazilian Lei Geral de Proteção de Dados (LGPD) of 2018/2020, the Argentine Data Protection Law No. 25.326 (2000), the Uruguayan Data Protection Law No. 18.331 (2008), the Peruvian Data Protection Regulation No. 29733 (2011), the Chilean data protection reform (Ley 21.719, expected to come into force in 2026) and other national press and media special laws.

**In Africa:** the Nigerian statutes (Data Protection Act 2023 and Press Council Act 1992), the Ghanaian Data Protection Act 2012, the South African Protection of Personal Information Act (POPIA), the Egyptian Data Protection Law No. 151/2020 and the press laws of the respective states apply.

**In the Middle East:** we orient ourselves to the data protection law of the United Arab Emirates (Federal Decree Law 45 of 2021 – Personal Data Protection Law), the Saudi Arabian Personal Data Protection Law (2021), the Qatari Law No. 13/2016 on the Protection of Privacy, the Omani Data Protection Law (Royal Decree 6/2022), the Bahraini press regulations (Decree Law 47/2002) and the respective criminal and media law provisions.

**In Asia:** depending on the edition, the Chinese Personal Information Protection Law (PIPL, 2021), the Indian Digital Personal Data Protection Act 2023, the Japanese Act on the Protection of Personal Information (APPI), the South Korean Personal Information Protection Act (PIPA) of 2011, the data protection laws of Hong Kong (PDPO), Singapore (PDPA 2012), Vietnam's Decree 13/2023 ND-CP, Malaysia's Personal Data Protection Act 2010, the Australian Privacy Act 1988 and the New Zealand Privacy Act 2020 apply.

All LEGIER editorial offices adhere to the press code, respect the limits of what is legally permissible and implement local youth protection, consumer protection and network security laws.

---

### Accessibility and Ethics

The "SCANDICDEV" app supports the provisions for accessibility: https://LegierGroup.com/en/vulnerable-customers

The "SCANDICDEV" app supports the provisions of the Modern Slavery Act: https://LegierGroup.com/en/modern-slavery-act

The "SCANDICDEV" app supports the provisions for AI ethics: https://ScandicData.com/ai-ethics

---

### Central contact points pursuant to the Digital Services Act (DSA)

Pursuant to Articles 11 and 12 DSA the "SCANDICDEV" app designates the following central contact points:

**Contact point for users (Art. 12 DSA):** LEGIER customer service – World Conference, e-mail: info@ScandicAssets.dev (German, English)

**Contact point for authorities (Art. 11 DSA):** LEGIER Beteiligungs GmbH – DSA contact point, e-mail: info@ScandicAssets.dev (German, English)

**Reporting illegal content:** Via the app function "Report content" or by e-mail to info@ScandicAssets.dev (All languages of the app)

Enquiries to these contact points will be processed within the statutory time limit. Complaints from users about editorial content can be submitted via an internal complaints system in the app; they may also contact arbitration boards or supervisory authorities.

---

### NetzDG (Network Enforcement Act)

The "SCANDICDEV" app complies with the statutory requirements of the Network Enforcement Act to improve law enforcement in social networks. Complaints about obviously unlawful content can be reported directly in the app. Reports are examined without delay and unlawful content is removed. Further information can be found in the "Complaints procedure" section of the app.

---

### Server locations and security

The servers for our digital offerings are located in high-security data centres in Boston (USA) and Zurich (Switzerland). Operation is carried out by the Swiss host ASPECTRA using modern encryption (AES with 4096-bit keys in CBC mode).

In addition, all data are mirrored in the data vaults of Iron Mountain and monitored around the clock by AI-based systems. In the event of an attack, operations can be relocated to redundant servers at short notice.

Payments for subscriptions are processed via certified payment service providers such as PayPal, Stripe, Klarna or credit card companies; credit card data are not stored on our servers.

---

### Notice on stock exchange information

Through exclusive partners, the "SCANDICDEV" supply international stock exchanges such as the Frankfurt Stock Exchange, the New York Stock Exchange, the Tokyo Stock Exchange, the London Stock Exchange and others. Stock prices and financial information are collected with the greatest possible care; however, no guarantee is given for timeliness, accuracy or completeness.

---

### Protection of minors

The "SCANDICDEV" supports initiatives for the protection of minors. The app offers filter and control options for parents and references to the ICRA classification (Internet Content Rating Association).

---

### Legal effect

This imprint is part of the digital offering of the "SCANDICDEV". Should individual formulations not correspond to the current legal situation, the remaining parts of the imprint remain unaffected in terms of content and validity.
  `
};

export default function Impressum() {
  const { language } = useLanguage();
  const location = useLocation();
  const content = language === 'de' ? contentDE : contentEN;
  const alternateLanguagePath = getAlternateLanguagePath(location.pathname);
  const homePath = getHomePath(language);
  const seo = legalPageSeo[location.pathname];

  // Simple markdown-like renderer
  const renderContent = (text) => {
    const lines = text.trim().split('\n');
    const elements = [];
    let key = 0;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      
      if (line.startsWith('## ')) {
        elements.push(
          <h1 key={key++} className="text-3xl sm:text-4xl font-bold text-white mb-6 mt-8 font-['Space_Grotesk']">
            {line.replace('## ', '')}
          </h1>
        );
      } else if (line.startsWith('### ')) {
        elements.push(
          <h2 key={key++} className="text-xl font-semibold text-white mt-10 mb-4 font-['Space_Grotesk'] border-b border-zinc-800 pb-2">
            {line.replace('### ', '')}
          </h2>
        );
      } else if (line.startsWith('**') && line.endsWith('**') && !line.includes(':')) {
        elements.push(
          <p key={key++} className="text-violet-400 font-medium mb-4">
            {line.replace(/\*\*/g, '')}
          </p>
        );
      } else if (line.startsWith('- **')) {
        const match = line.match(/- \*\*(.+?)\*\*(.+)/);
        if (match) {
          elements.push(
            <div key={key++} className="mb-3 pl-4 border-l-2 border-violet-500/30">
              <strong className="text-white">{match[1]}</strong>
              <span className="text-zinc-300">{match[2]}</span>
            </div>
          );
        }
      } else if (line.startsWith('**') && line.includes(':**')) {
        const match = line.match(/\*\*(.+?):\*\*(.+)/);
        if (match) {
          elements.push(
            <p key={key++} className="mb-3">
              <strong className="text-white">{match[1]}:</strong>
              <span className="text-zinc-300">{match[2]}</span>
            </p>
          );
        }
      } else if (line === '---') {
        // Skip dividers, we use spacing instead
      } else if (line.trim() === '') {
        // Skip empty lines
      } else {
        elements.push(
          <p key={key++} className="text-zinc-300 mb-3 leading-relaxed">
            {line}
          </p>
        );
      }
    }

    return elements;
  };

  return (
    <div className="min-h-screen bg-[#0A0A0B] text-zinc-300">
      <Seo
        title={seo?.title || content.title}
        description={seo?.description || content.title}
        path={location.pathname}
        language={language}
      />
      <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#0A0A0B]/85 border-b border-zinc-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between gap-3 h-20 md:h-24">
            <Link to={homePath} aria-label={content.back} className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline">{content.back}</span>
            </Link>
            <ScandicDevLogo className="h-14 w-auto object-contain md:h-16" />
            <LanguageToggleLink language={language} href={alternateLanguagePath} />
          </div>
        </div>
      </header>

      <main className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <article className="max-w-4xl mx-auto">
          {renderContent(content.content)}
          <p className="text-sm text-zinc-500 mt-12 pt-8 border-t border-zinc-800">{content.lastUpdated}</p>
        </article>
      </main>
    </div>
  );
}
